import toDataUrl from './toDataUrl.ts';

export type PreloadImagesInElementOptions = {
  shouldCenterImages?: boolean;
};

/**
 * Fetches each <img> element's src and replaces it with an inline data URL.
 * This ensures images are embedded directly in the DOM before snapdom captures,
 * avoiding Safari's inability to resolve HTTP URLs inside SVG foreignObject data URIs.
 * Images that no longer exist upstream are removed so nothing is drawn in their place.
 *
 * Images are centred by default. Pass shouldCenterImages: false for content that
 * already carries its own alignment, since these inline styles outrank any stylesheet.
 */
const preloadImagesInElement = async (
  element: HTMLElement,
  { shouldCenterImages = true }: PreloadImagesInElementOptions = {}
): Promise<void> => {
  const imageNodes = Array.from(element.querySelectorAll('img'));

  if (shouldCenterImages) {
    imageNodes.forEach((img) => {
      img.style.display = 'block';
      img.style.margin = '0 auto';
    });
  }

  const tasks = imageNodes.map(async (img) => {
    const src = img.getAttribute('src');
    if (!src) return;

    if (!src.startsWith('data:') && !src.startsWith('blob:')) {
      try {
        const dataUrl = await toDataUrl(src);
        if (dataUrl === null) {
          img.remove();
          return;
        }
        img.setAttribute('src', dataUrl);
      } catch (error) {
        console.error(`Failed to preload image: ${src}`, error);
        throw error;
      }
    }

    await img.decode();
  });

  await Promise.all(tasks);
};

export default preloadImagesInElement;
